import { NgClass, ViewportScroller } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  effect,
  inject,
  Renderer2,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { filterSuccess } from '@ngneat/query';
import { isAfter, isBefore } from 'date-fns';
import { filter, switchMap, tap } from 'rxjs';
import { ThemeRepository } from 'src/app/_core';
import { CategoryImagesRepository } from 'src/app/_core/repositories/category-images.repository';
import { EcommerceService } from 'src/app/_core/services/ecommerce.service';
import { TicketService } from 'src/app/_core/services/ticket.service';
import { WebsiteConfigService } from 'src/app/_core/services/website-config.service';
import { environment } from 'src/environments/environment';
import { PricingRepository } from './_core/repositories/pricing.repository';
import { AnalyticsService } from './_core/services/analytics.service';
import {
  DeviceTypeEnum,
  WindowResizeService,
  WindowResolutionData,
} from './_core/services/window-resize.service';

@Component({
  standalone: true,
  selector: 'app-root',
  template: `
    @if (getWebsiteConfig()?.data?.environment?.isTestEnvironment) {
    <div
      class="bg-yellow-600 text-white text-center py-1 text-xs z-[9999] fixed left-0 right-0"
      [ngClass]="{
        'top-0':
          getWebsiteConfig()?.data?.environment?.bannerPosition === 'top',
        'bottom-0':
          getWebsiteConfig()?.data?.environment?.bannerPosition === 'bottom'
      }"
    >
      {{ getWebsiteConfig()?.data?.environment?.bannerText }}
    </div>
    }
    <router-outlet />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, NgClass],
})
export class AppComponent {
  private readonly _windowResizeService = inject(WindowResizeService);
  private readonly _viewPort = inject(ViewportScroller);
  private readonly _websiteConfigService = inject(WebsiteConfigService);
  private readonly _themeRepository = inject(ThemeRepository);
  private readonly _pricingRepository = inject(PricingRepository);
  private readonly _renderer = inject(Renderer2);
  private readonly _categoryImagesRepository = inject(CategoryImagesRepository);
  private readonly _document = inject(DOCUMENT);
  private readonly _ticketService = inject(TicketService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _ecommerceService = inject(EcommerceService);
  private readonly _analyticsService = inject(AnalyticsService);

  private injectedIds = new Set<string>();

  getCategoryImages = toSignal(
    this._websiteConfigService.getCategoryImages().result$.pipe(
      tap(({ data }) => {
        if (data?.categoryImages?.length) {
          const categories = data.categoryImages
            .filter((categoryImage: any) => !!categoryImage.image)
            .map((categoryImage: any) => {
              return {
                categoryId: categoryImage.categoryId,
                imageUrl: categoryImage.image.url,
                imageAlt: categoryImage.image.alt,
              };
            });
          this._categoryImagesRepository.setCategories(categories);
        }
      })
    )
  );

  getWebsiteConfig = toSignal(
    this._route.queryParams.pipe(
      switchMap((queryParams) => {
        return this._websiteConfigService
          .getWebsiteConfig(Boolean(queryParams['draft']))
          .result$.pipe(
            filterSuccess(),
            tap(({ data }) => {
              this._renderer.setStyle(
                this._document.body,
                'color',
                data.theme.text
              );
              this._renderer.setStyle(
                this._document.body,
                'backgroundColor',
                data.theme.bgBase
              );
              if (data.favicon) {
                this._setFavicon(data.favicon.url);
              }
              this._themeRepository.setStore({
                primary: data.theme.primary,
                primaryBackground: data.theme.primaryBackground,
                text: data.theme.text,
                textLight: data.theme.textLight,
                secondary: data.theme.secondary,
                secondaryBackground: data.theme.secondaryBackground,
                accent: data.theme.accent,
                accentBackground: data.theme.accentBackground,
                lightBorder: data.theme.lightBorder,
                bgSurface: data.theme.bgSurface,
                bgContent: data.theme.bgContent,
                bgBase: data.theme.bgBase,
                logoPath: data.logo?.url || '',
                logoAlt: data.logo?.alt || '',
                mailTo: data.mailTo || '',
                websiteTitle: data.websiteTitle,
              });
            })
          );
      })
    )
  );

  queryEcommerceInstance = toSignal(
    this._ecommerceService
      .queryEcommerceInstance(environment.ecommerceInstanceId)
      .result$.pipe(
        tap(({ data }) => {
          if (data) {
            if (data.priceLists.length) {
              const pricing =
                data.priceLists.find(
                  (v) =>
                    isAfter(
                      new Date(),
                      new Date(v.validPeriod.validFromDate)
                    ) &&
                    isBefore(new Date(), new Date(v.validPeriod.validUntilDate))
                ) || data.priceLists?.[0];

              this._pricingRepository.setPriceListId(pricing.id);
            }

            this._pricingRepository.setStore({
              hasFacebook: data.hasFacebookLogin,
              hasGoogle: data.hasGoogleLogin,
              domain: data.ecommerceUrl,
            });
          }
        })
      )
  );

  queryPriceList = toSignal(
    this._pricingRepository.priceListId$.pipe(
      filter((priceListId) => !!priceListId),
      switchMap((priceListId) => {
        return this._ticketService.queryPriceList(priceListId!).result$;
      })
    )
  );

  queryAnalytics = toSignal(
    this._analyticsService.queryAnalytics().result$.pipe(
      tap((res) => {
        if (environment.production && res.data?.codes?.length) {
          res.data.codes
            .filter((item) => !this.injectedIds.has(item.id) && item.isActive)
            .forEach((item) => {
              const target =
                item.position === 'head'
                  ? this._document.head
                  : this._document.body;

              const temp = this._document.createElement('div');
              temp.innerHTML = item.code;

              Array.from(temp.children).forEach((el) => {
                if (el.tagName === 'SCRIPT') {
                  this._appendScript(el as HTMLScriptElement, target);
                } else {
                  const cloned = el.cloneNode(true);
                  this._renderer.appendChild(target, cloned);
                }
              });

              this.injectedIds.add(item.id);
            });
        }
      })
    )
  );

  constructor() {
    effect(() => {
      this._setScrollOffset(this._windowResizeService.windowResolutionData());
    });
  }

  private _setScrollOffset(data: WindowResolutionData) {
    this._viewPort?.setOffset([
      0,
      data.device === DeviceTypeEnum.DESKTOP ? 88 : 55,
    ]);
  }

  private _setFavicon(faviconUrl: string): void {
    let link: HTMLLinkElement | null =
      this._document.querySelector("link[rel*='icon']");

    if (link) {
      this._renderer.setAttribute(link, 'href', faviconUrl);
    } else {
      link = this._renderer.createElement('link');
      this._renderer.setAttribute(link, 'rel', 'icon');
      this._renderer.setAttribute(link, 'type', 'image/x-icon');
      this._renderer.setAttribute(link, 'href', faviconUrl);
      this._renderer.appendChild(this._document.head, link);
    }
  }

  private _appendScript(scriptEl: HTMLScriptElement, target: HTMLElement) {
    const newScript = this._renderer.createElement('script');
    const src = scriptEl.getAttribute('src');

    if (src) {
      newScript.src = src;
      if (scriptEl.async) newScript.async = true;
      if (scriptEl.defer) newScript.defer = true;
    } else {
      newScript.text = scriptEl.innerHTML;
    }

    Array.from(scriptEl.attributes).forEach((attr) =>
      newScript.setAttribute(attr.name, attr.value)
    );

    this._renderer.appendChild(target, newScript);
  }
}
